/**
 * Nutrition's own vocabulary: the shape two vendor endpoints and four question
 * kinds flatten onto, and the two closed sets a reader can switch between from
 * the tweak panel.
 *
 * A food and a recipe are one card model here for the same reason nine
 * marketplaces are one product: both are an edible thing with an energy figure,
 * a macro split and a serving the figure is stated against. What differs is
 * optional and stays optional — a recipe carries a picture, directions,
 * ingredients and two clocks; a food carries a brand, a vendor kind and up to
 * ten alternative servings.
 *
 * ONE rule has teeth here above all others, and it is the vendor's, not ours:
 * THE TWO ENDPOINTS DISAGREE ABOUT THEIR MICRONUTRIENT UNITS. A food serving's
 * vitamin A, vitamin C, calcium and iron are metric amounts (mcg for vitamin A,
 * mg for the rest); a recipe's four are PERCENTAGES OF DAILY INTAKE. `28` and
 * `0` sit in the same slot meaning micrograms on one card and per cent on the
 * next, so the two shapes never merge: the basis rides the panel and every
 * figure is printed with the unit its own endpoint measured it in.
 *
 * The second rule is the serving. Three captured food rows are stated `Per
 * 101g`, `Per 100g` and `Per 4 oz` — three different bases in one three-row
 * set — so a bare `197` beside a bare `110` is not a comparison. The basis is
 * carried with the figure, and where it is not, the set's own frame line says
 * it once for everything under it.
 */
import type { ReactNode } from "react";
import type {
  CresultcardAsset,
  CresultcardDetailFrame,
  CresultcardOpenControl,
} from "@/components/ui/Cresultcard";

/** Which of the two endpoints answered. Never inferred from which fields arrived. */
export enum CnutritioncardKind {
  Food = "food",
  Recipe = "recipe",
}

/**
 * The family's three compositions of one edible thing. They differ in
 * structure, not in decoration: one is the dense label line, one leads with the
 * dish photograph, and one drops media entirely for the aligned macro figures.
 */
export enum CnutritioncardVariant {
  /** The reference composition: identity first, the vendor's own summary line. */
  LabelLine = "Label line",
  /** Media-led: the recipe photograph with the identity beneath it. */
  PlateCard = "Plate card",
  /** No media at all: the four macros as a column-aligned board. */
  MacroBoard = "Macro board",
}

/**
 * Where the headline energy figure says what it is measured against.
 *
 * Both readings are honest and both are one spelling, so both ship. `Stated`
 * puts `per 101 g` under every figure, which is the only way to compare rows
 * whose bases genuinely differ; `Frame only` lets the set's own context line say
 * it once and gives the room back to the name.
 */
export enum CnutritioncardServingScope {
  /** `per 101 g` under the figure, on every headline surface. */
  Stated = "Serving stated",
  /** Nothing under the figure; the set's own context line says it once. */
  FrameOnly = "Frame line only",
}

/**
 * Which unit system a micronutrient panel was measured in.
 *
 * It is DATA rather than a reading: nobody chooses it, the endpoint that
 * answered does. A panel without it is four unlabelled numbers.
 */
export enum CnutritioncardMicroBasis {
  /** `food.get.v4` — vitamin A in mcg, the other three in mg. */
  MetricAmount = "metricAmount",
  /** `recipe.get.v2` — all four as a percentage of daily intake. */
  DailyPercent = "dailyPercent",
}

/** The four micronutrients both endpoints publish, under two different units. */
export enum CnutritioncardMicronutrient {
  VitaminA = "vitaminA",
  VitaminC = "vitaminC",
  Calcium = "calcium",
  Iron = "iron",
}

/** The macro and mineral roster both endpoints agree the units of. */
export interface CnutritioncardNutrients {
  /** kcal. */
  readonly calories: number | null;
  /** Grams. */
  readonly carbohydrate: number | null;
  readonly protein: number | null;
  readonly fat: number | null;
  readonly saturatedFat: number | null;
  readonly transFat: number | null;
  readonly polyunsaturatedFat: number | null;
  readonly monounsaturatedFat: number | null;
  readonly fiber: number | null;
  readonly sugar: number | null;
  /** Grams, and food-only: the recipe endpoint publishes no added-sugar figure. */
  readonly addedSugars: number | null;
  /** Milligrams. */
  readonly cholesterol: number | null;
  readonly sodium: number | null;
  readonly potassium: number | null;
}

/** The four figures whose unit depends on which endpoint answered. */
export interface CnutritioncardMicronutrients {
  readonly basis: CnutritioncardMicroBasis;
  readonly vitaminA: number | null;
  readonly vitaminC: number | null;
  readonly calcium: number | null;
  readonly iron: number | null;
}

/**
 * One serving, and everything measured against it.
 *
 * A food publishes up to ten of these — `100 g`, `1 cup cooked, diced`,
 * `1 oz, with bone cooked (yield after bone removed)` — and a recipe publishes
 * exactly one, its own `1 serving`. They are the same shape because they answer
 * the same question, and the count is what differs.
 */
export interface CnutritioncardServing {
  readonly key: string;
  /** The household measure, verbatim. Up to 49 characters observed. */
  readonly description: string | null;
  readonly metricAmount: number | null;
  readonly metricUnit: string | null;
  readonly nutrients: CnutritioncardNutrients;
  /** `null` where the endpoint published no micronutrients at all. */
  readonly micronutrients: CnutritioncardMicronutrients | null;
}

/** One ingredient, keeping the edge that resolves it into its own panel. */
export interface CnutritioncardIngredient {
  /** The handle `fatsecret.foodDetail` takes back. The graph edge, not a label.
   *  Null where the record was read back off a persisted attach block, which
   *  carries the line the author wrote and no vendor handle behind it. */
  readonly foodId: string | null;
  readonly name: string;
  /** The amount as authored: `1 cup creamy peanut butter`. */
  readonly description: string | null;
}

/**
 * One edible thing, as both endpoints can describe it.
 *
 * Anything one endpoint alone carries is optional, and absent means absent:
 * there is no placeholder, no ghost slot and no invented default. `summary` and
 * `description` are separate fields rather than one because they are not the
 * same claim — the first is the vendor's own unit-bound arithmetic
 * (`Per 101g - Calories: 197kcal | …`) and the second is a human's blurb about
 * a dish.
 */
export interface CnutritioncardData {
  /** The vendor's own id. The row key and the drill handle. */
  readonly key: string;
  readonly kind: CnutritioncardKind;
  /** The only field both endpoints always fill. */
  readonly name: string;
  /** Food only, and only on a `Brand` row: one of three captured rows carries it. */
  readonly brand: string | null;
  /** The vendor's own food kind, verbatim: `Generic` or `Brand`. */
  readonly vendorKind: string | null;
  /** The food search row's unit-bound one-liner, verbatim. Never re-derived. */
  readonly summary: string | null;
  /** The recipe's own blurb. 35 to 65 characters on the captured rows. */
  readonly description: string | null;
  readonly href: string | null;
  /** The vendor's own image URLs, in wire order. `images[0]` IS the thumbnail. */
  readonly images: readonly CresultcardAsset[];
  /** Every serving the endpoint published, in wire order. */
  readonly servings: readonly CnutritioncardServing[];
  /** Recipe only: how many servings the whole recipe yields. */
  readonly numberOfServings: number | null;
  readonly preparationTimeMinutes: number | null;
  readonly cookingTimeMinutes: number | null;
  readonly gramsPerPortion: number | null;
  readonly types: readonly string[];
  readonly categories: readonly string[];
  readonly directions: readonly string[];
  readonly ingredients: readonly CnutritioncardIngredient[];
}

export interface CnutritioncardProps {
  readonly data: CnutritioncardData;
  /** Omitted, the family reads the tweak panel's registry. */
  readonly variant?: CnutritioncardVariant;
  readonly servingScope?: CnutritioncardServingScope;
  readonly openControl?: CresultcardOpenControl;
}

/** What every card variant receives once the selection is resolved. */
export interface CnutritioncardVariantProps {
  readonly data: CnutritioncardData;
  readonly servingScope: CnutritioncardServingScope;
  readonly openControl?: CresultcardOpenControl;
}

export interface CnutritioncardRowProps {
  readonly data: CnutritioncardData;
  readonly onLast?: boolean;
  readonly openControl?: CresultcardOpenControl;
}

/**
 * The rail card takes NO card variant. It is one fixed composition — a deck
 * card is a comparison object, and the three card variants are three answers to
 * "this thing, on its own" — so it stamps its own name.
 */
export interface CnutritioncardRailProps {
  readonly data: CnutritioncardData;
  readonly servingScope?: CnutritioncardServingScope;
  readonly openControl?: CresultcardOpenControl;
}

export interface CnutritioncardDetailProps {
  readonly data: CnutritioncardData;
  /** The frame every value below is stated against: the endpoint, the ask, the capture. */
  readonly context: string;
  /** What bounds this projection. Wraps under the frame; never truncated. */
  readonly bounds?: ReactNode;
  /** ABSENT where the header context already states it. */
  readonly provenance?: string;
  readonly servingScope?: CnutritioncardServingScope;
  /** `Embedded` where the detail IS a set's drill pane. */
  readonly frame?: CresultcardDetailFrame;
}

/** The row's meta facts, named so the drop order is declared against names. */
export enum CnutritioncardMetaSlot {
  Kind = "kind",
  Brand = "brand",
  Serving = "serving",
  Yield = "yield",
  Time = "time",
}
